import { useQuery, type QueryClient } from '@tanstack/react-query';
import { api, ApiError, unwrap, type Schemas } from '../../../api/client';
import type { Ledger } from './labels';

/** The OpenAPI schema marks Payment as nullable (`type: [object, null]`); real responses never are. */
export type Payment = NonNullable<Schemas['Payment']>;
export type PaymentRow = Schemas['PaymentList']['data'][number];
export type PaymentMethod = Schemas['PaymentMethod'];
export type Allocation = Schemas['Allocation'];
export type OpenItems = Schemas['OpenItems'];
export type OpenClaim = OpenItems['claims'][number];

/** Header params for endpoints whose schema requires an explicit Idempotency-Key. */
export function idempotency() {
  return { header: { 'idempotency-key': crypto.randomUUID() } };
}

export const keys = {
  payments: ['payments'] as const,
  payment: (id: string) => ['payments', 'detail', id] as const,
  methods: ['payment-methods'] as const,
  openItems: (contactId: string, ledger: Ledger) => ['open-items', contactId, ledger] as const,
};

export function usePaymentMethods() {
  return useQuery({
    queryKey: keys.methods,
    queryFn: () => unwrap(api.GET('/payment-methods')),
    staleTime: 5 * 60_000,
  });
}

/** Unwraps a response that must contain a payment (see `Payment`). */
export async function unwrapPayment(
  promise: Promise<{ data?: Schemas['Payment']; error?: unknown; response: Response }>,
): Promise<Payment> {
  const p = await unwrap(promise);
  if (!p) throw new ApiError(500, 'EMPTY_RESPONSE', 'The server returned no payment');
  return p;
}

export function useOpenItems(contactId: string | null | undefined, ledger: Ledger) {
  return useQuery({
    queryKey: keys.openItems(contactId ?? '', ledger),
    queryFn: () =>
      unwrap(
        api.GET('/contacts/{id}/open-items', {
          params: { path: { id: contactId ?? '' }, query: { ledger } },
        }),
      ),
    enabled: Boolean(contactId),
  });
}

/**
 * Anything that moves money or allocations changes documents of other screens too (invoice payment
 * status, contact balances, reports), so refresh them all.
 */
export function invalidateMoney(qc: QueryClient): Promise<void> {
  const prefixes = [
    'payments',
    'open-items',
    'contacts',
    'sales-invoices',
    'supplier-invoices',
    'credit-notes',
    'debit-notes',
    'sales-returns',
    'purchase-returns',
    'goods-receipts',
    'reports',
    'ledger',
  ];
  return Promise.all(prefixes.map((p) => qc.invalidateQueries({ queryKey: [p] }))).then(
    () => undefined,
  );
}
